<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  transfers: { type: Array, default: () => [] },
});

const emit = defineEmits(['download']);
const { t } = useI18n();

const transferGroups = computed(() => {
  const groups = new Map();

  props.transfers.forEach(transfer => {
    const date = transfer.data_transferencia || transfer.registrado_em || '';
    const key = date.slice(0, 10) || 'unknown';
    const group = groups.get(key) || { date, transfers: [] };
    group.transfers.push(transfer);
    groups.set(key, group);
  });

  return [...groups.values()];
});

const formatDate = value => {
  if (!value) return t('REAL_ESTATE_FINANCE.DETAIL.EMPTY_VALUE');
  if (!/^\d{4}-\d{2}-\d{2}/.test(value)) return value;

  const [year, month, day] = value.slice(0, 10).split('-');
  return `${day}/${month}/${year}`;
};
const beneficiaryName = transfer =>
  transfer.beneficiario?.name || t('REAL_ESTATE_FINANCE.DETAIL.EMPTY_VALUE');
const amount = transfer =>
  transfer.valor?.amount || t('REAL_ESTATE_FINANCE.COMMON.EMPTY_MONEY');
const receiptName = transfer => transfer.comprovante?.nome;
const canDownloadReceipt = transfer =>
  Boolean(
    transfer.id && receiptName(transfer) && transfer.comprovante?.pode_baixar
  );
</script>

<template>
  <div class="contents">
    <section
      v-if="transferGroups.length"
      data-testid="finance-installment-transfer-receipts"
      class="rounded-lais border border-lais-border-soft bg-white p-4 shadow-lais-card"
    >
      <h3 class="font-semibold text-lais-text">
        {{ t('REAL_ESTATE_FINANCE.DETAIL.TRANSFER_RECEIPTS') }}
      </h3>
      <div
        v-for="(group, groupIndex) in transferGroups"
        :key="group.date"
        data-transfer-group
        class="mt-4 first:mt-3"
      >
        <p class="text-sm font-semibold text-lais-text-muted">
          {{ formatDate(group.date) }}
        </p>
        <div
          class="mt-3 hidden grid-cols-4 gap-4 border-b border-lais-border-soft pb-2 text-xs font-bold uppercase tracking-wide text-lais-text-muted sm:grid"
        >
          <span>{{ t('REAL_ESTATE_FINANCE.DETAIL.TRANSFER_RECIPIENT') }}</span>
          <span>{{ t('REAL_ESTATE_FINANCE.DETAIL.TRANSFER_SENT') }}</span>
          <span>{{ t('REAL_ESTATE_FINANCE.DETAIL.TRANSFER_AMOUNT') }}</span>
          <span>{{ t('REAL_ESTATE_FINANCE.DETAIL.TRANSFER_FILE') }}</span>
        </div>
        <div class="divide-y divide-lais-border-soft">
          <article
            v-for="transfer in group.transfers"
            :key="transfer.id"
            data-transfer-row
            class="grid gap-2 py-4 first:pt-3 sm:grid-cols-4 sm:gap-4"
          >
            <div>
              <p class="text-xs font-semibold text-lais-text-muted sm:hidden">
                {{ t('REAL_ESTATE_FINANCE.DETAIL.TRANSFER_RECIPIENT') }}
              </p>
              <p class="mt-1 text-sm font-semibold text-lais-text sm:mt-0">
                {{ beneficiaryName(transfer) }}
              </p>
            </div>
            <div>
              <p class="text-xs font-semibold text-lais-text-muted sm:hidden">
                {{ t('REAL_ESTATE_FINANCE.DETAIL.TRANSFER_SENT') }}
              </p>
              <p class="mt-1 text-sm text-lais-text sm:mt-0">
                {{ formatDate(transfer.data_transferencia) }}
              </p>
            </div>
            <div>
              <p class="text-xs font-semibold text-lais-text-muted sm:hidden">
                {{ t('REAL_ESTATE_FINANCE.DETAIL.TRANSFER_AMOUNT') }}
              </p>
              <p class="mt-1 text-sm text-lais-text sm:mt-0">
                {{ amount(transfer) }}
              </p>
            </div>
            <div>
              <p class="text-xs font-semibold text-lais-text-muted sm:hidden">
                {{ t('REAL_ESTATE_FINANCE.DETAIL.TRANSFER_FILE') }}
              </p>
              <button
                v-if="canDownloadReceipt(transfer)"
                data-download-receipt
                type="button"
                class="mt-1 max-w-full truncate text-left text-sm font-semibold text-lais-primary hover:underline sm:mt-0"
                @click="emit('download', transfer)"
              >
                {{ receiptName(transfer) }}
              </button>
              <p
                v-else
                class="mt-1 truncate text-sm text-lais-text-muted sm:mt-0"
              >
                {{
                  receiptName(transfer) ||
                  t('REAL_ESTATE_FINANCE.DETAIL.EMPTY_VALUE')
                }}
              </p>
            </div>
          </article>
        </div>
        <div
          v-if="groupIndex < transferGroups.length - 1"
          class="border-b border-lais-border-soft"
        />
      </div>
    </section>
  </div>
</template>
